import React, { useState } from "react";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { Comment } from "../../../types";
import { formatDistanceToNow } from "date-fns";

interface CommentDetailProps {
  comment: Comment;
  onBack: () => void;
  onResolve: (commentId: string) => void;
  onDelete: (commentId: string) => void;
  onReply: (commentId: string, text: string) => void;
}

export const CommentDetail: React.FC<CommentDetailProps> = ({
  comment,
  onBack,
  onResolve,
  onDelete,
  onReply,
}) => {
  const [replyText, setReplyText] = useState("");
  const [isReplying, setIsReplying] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleReply = () => {
    if (replyText.trim()) {
      onReply(comment.id, replyText);
      setReplyText("");
      setIsReplying(false);
    }
  };

  const handleCancel = () => {
    setReplyText("");
    setIsReplying(false);
  };

  const handleStartReply = () => {
    setIsReplying(true);
  };

  const handleDelete = async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      await onDelete(comment.id);
      // Navigate back to the list after successful deletion
      onBack();
    } catch (error) {
      console.error("Failed to delete comment:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 flex items-center justify-between border-b border-gray-100">
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          <span className="text-sm">Back</span>
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="text-gray-600 hover:text-red-600 transition-colors disabled:opacity-50"
            title="Delete comment"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => onResolve(comment.id)}
            className="text-gray-600 hover:text-green-600 transition-colors"
            title="Resolve comment"
          >
            <CheckCircleIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          <div className="flex flex-col gap-1">
            <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center">
              <span
                className="text-xs font-medium"
                style={{ color: "#ffffff" }}
              >
                {comment.number}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{comment.author}</span>
              {!comment.isRead && comment.status === "active" && (
                <span className="px-2 py-0.5 text-xs font-medium bg-coral-100 text-coral-600 rounded-full">
                  New
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500">
              {formatDistanceToNow(new Date(comment.timestamp), {
                addSuffix: true,
              })}
            </p>
            <p className="text-sm text-gray-900 mt-1">{comment.text}</p>

            {/* Screenshot display */}
            {comment.screenshot_url && (
              <div className="mt-3">
                <img
                  src={comment.screenshot_url}
                  alt="Comment context screenshot"
                  className="w-full rounded-lg border border-gray-200 shadow-sm"
                  style={{ maxHeight: "200px", objectFit: "cover" }}
                  onError={(e) => {
                    console.error(
                      "Failed to load screenshot:",
                      comment.screenshot_url
                    );
                    e.currentTarget.style.display = "none";
                  }}
                  onLoad={() => {
                    console.log(
                      "Screenshot loaded successfully:",
                      comment.screenshot_url
                    );
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">
                  📸 Screenshot when comment was created
                </p>
              </div>
            )}
          </div>

          <div className="mt-4">
            {!isReplying ? (
              <button
                onClick={handleStartReply}
                className="w-full p-2 text-sm text-gray-500 border border-gray-200 rounded-lg hover:border-purple-300 hover:text-purple-600 transition-colors"
              >
                Add a reply...
              </button>
            ) : (
              <>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Add a reply..."
                  className="w-full p-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-500"
                  rows={2}
                  autoFocus
                />
                <div className="mt-2 flex justify-end gap-2">
                  <button
                    onClick={handleCancel}
                    className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReply}
                    disabled={!replyText.trim()}
                    className="px-3 py-1.5 text-xs bg-purple-500 rounded-md hover:bg-purple-600 disabled:opacity-50 transition-colors"
                    style={{ color: "#ffffff" }}
                  >
                    Post
                  </button>
                </div>
              </>
            )}
          </div>

          {comment.replies.length > 0 && (
            <div className="mt-6 space-y-4">
              {comment.replies.map((reply) => (
                <div key={reply.id} className="pl-4 border-l-2 border-gray-100">
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-medium">{reply.author}</span>
                    <p className="text-xs text-gray-500">
                      {formatDistanceToNow(new Date(reply.timestamp), {
                        addSuffix: true,
                      })}
                    </p>
                    <p className="text-sm text-gray-900">{reply.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
